import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ILegalClientFullObject } from '../Interfaces/ILegalClient';
import type { State } from '../redux/mainReducer';
import { ICourt } from '../Interfaces/ICourt';
import { useGetCourt } from '../hooks/reactQuerys/CourtQueries';
import { HandelsregisterSearch } from '../generic/Handelsregsiter/HandelsregisterSearch';
import { Search } from '@mui/icons-material';
import type { ILegalClientGroup } from '../Interfaces/ILegalClientGroup';
import { useGetLegalClientGroups } from '../hooks/reactQuerys/LegalClientGroups';
import { LegalClientGroupSelector } from '../LegalClientGroup/LegalClientGroupSelector';

type IProps = {
  formValues: ILegalClientFullObject;
  setFormValues: Dispatch<SetStateAction<ILegalClientFullObject>>;
};

export const LegalPersonForm = ({ formValues, setFormValues }: IProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [
    wasSuccessfullImportedFromhandelsregister,
    setWasSuccessfullImportedFromhandelsregister,
  ] = useState(false);

  const { legalClientGroups = [] } = useGetLegalClientGroups();


  const businessForms = useSelector(
    (state: State) => state.businessFormArray.storeBusinessForm
  );

  const { courtArray = [], isLoadingCourtArray } = useGetCourt();

  const selectedCourt = useMemo<ICourt | null>(() => {
    if (!formValues.idCourt) return null;
    return courtArray.find(c => c.idCourt === formValues.idCourt) ?? null;
  }, [courtArray, formValues.idCourt]);

  const handleFieldChangeOnImport = (
    index: number,
    updates: { [key: string]: string }
  ) => {
    setFormValues({
      ...formValues,
      LastName:
        updates['Function_Field2'] === ''
          ? formValues.LastName
          : updates['Function_Field2'],
      RegisterNumber:
        updates['Res_Field1'] === ''
          ? formValues.RegisterNumber
          : updates['Res_Field1'],
    });
  };

  return (
    <>
      <HandelsregisterSearch
        openImportFromHandelsregister={isSearchOpen}
        setOpenImportFromHandelsregister={setIsSearchOpen}
        wasSuccessfullImportedFromhandelsregister={
          wasSuccessfullImportedFromhandelsregister
        }
        setWasSuccessfullImportedFromhandelsregister={
          setWasSuccessfullImportedFromhandelsregister
        }
        handleFieldChangeOnImport={handleFieldChangeOnImport}
        index={0}
        isXMLImport={false}
      />

      <Grid item xs={3}>
        <LegalClientGroupSelector
          value={formValues.LegalClientGroups}
          options={legalClientGroups}
          onChange={(newValue) =>
            setFormValues(prev => ({ ...prev, LegalClientGroups: newValue }))
          }
        />
      </Grid>

      <Grid item xs={6} />

      <Grid item xs={3}>
        <TextField
          select
          label='Rechtsform'
          size='small'
          fullWidth
          value={formValues.idBusinessForm || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const businessFormId = Number(event.target.value);

            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idBusinessForm: businessFormId,
              BusinessForm: businessForms.find(
                businessForm => businessForm.idBusinessForm === businessFormId
              )?.BusinessForm,
            }));
          }}
        >
          <MenuItem key={`idBusinessForm-null}`} value={-1}>
            <i>Keine Rechtsform zugeordnet</i>
          </MenuItem>
          {businessForms.map((bf, i) => (
            <MenuItem key={i} value={bf.idBusinessForm} disabled={bf.idBusinessForm === 1}>
              {bf.BusinessForm}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={9}>
        <TextField
          required
          label='Firmenname'
          value={formValues.LastName || ''}
          size='small'
          fullWidth
          onChange={event => {
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              LastName: event.target.value,
            }));
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>

      <Grid item xs={6}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Gründungsdatum'
          size='small'
          value={formValues.Birthdate || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthdate: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label='Gründungsort'
          size='small'
          value={formValues.Birthplace || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthplace: event.target.value,
            }))
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Auflösungsdatum'
          size='small'
          value={formValues.DateOfDeath || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              DateOfDeath: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label='Auflösungsort'
          size='small'
          value={formValues.PlaceOfDeath || ''}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              PlaceOfDeath: event.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>

      <Grid item xs={3}>
        <TextField
          label='Steuernummer'
          size='small'
          value={formValues.TaxNumber || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              TaxNumber: event.target.value,
            }))
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          label='Umsatzsteuer-ID'
          size='small'
          value={formValues.SalesTaxNumber || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              SalesTaxNumber: event.target.value,
            }))
          }
          fullWidth
        />
      </Grid>

      <Grid item xs={3} sx={{ display: 'flex' }}>
        <TextField
          label='Registernummer'
          size='small'
          value={formValues.RegisterNumber || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              RegisterNumber: event.target.value,
            }))
          }
          fullWidth
        />
        <IconButton onClick={() => setIsSearchOpen(true)}>
          <Search />
        </IconButton>
      </Grid>
      <Grid item xs={3}>
        <Autocomplete<ICourt>
          options={courtArray}
          loading={isLoadingCourtArray}
          value={selectedCourt}
          onChange={(_, newValue) => {
            setFormValues(prev => ({
              ...prev,
              idCourt: newValue ? newValue.idCourt : null,
            }));
          }}
          getOptionLabel={option => option?.Court ?? ''}
          isOptionEqualToValue={(opt, val) => opt.idCourt === val.idCourt}
          renderInput={params => (
            <TextField {...params} label='Amtsgericht' size='small' fullWidth />
          )}
        />
      </Grid>
      {/*
      <Grid item xs={3}>
        <TextField
          label='E-Mail-Adresse'
          size='small'
          type='email'
          value={formValues.Email}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Email: event.target.value,
            }))
          }
          fullWidth
          required
        />
      </Grid>
       */}

      <Grid
        item
        xs={3}
        display='flex'
        justifyContent='center'
        alignItems='center'
      >
        {formValues.idArnoTopIntern && (
          <Box>
            <strong>Namen.Nr.:</strong> {formValues.idArnoTopIntern}
          </Box>
        )}
      </Grid>
    </>
  );
};
